import {
  Dialog,
  DialogContent,
  DialogTitle,
  FilterOptionsState,
  FormControl,
  Input,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { UserCommunicationPreference } from '@provision/entity-types';
import { getFullName } from '@provision/formatting-utils';
import { useCallback, useEffect, useMemo } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { StyledLabel } from 'src/components/AddClientModal/dynamic-property-element';
import { AvatarUploader } from 'src/components/CreateUserModal/avatar-uploader';
import {
  AvatarFlushFailureReason,
  AvatarFlushStatus,
  useAvatarSelection,
} from 'src/components/CreateUserModal/use-avatar-selection';
import { UserModalActions } from 'src/components/CreateUserModal/user-modal-actions';
import { FormAutocomplete, MAX_NUMBER_LENGTH, MIN_NUMBER_LENGTH } from 'src/components/Forms';
import { PHONE_FIELD_PLACEHOLDER } from 'src/components/Forms/const';
import { TextMaskPhone } from 'src/components/Forms/FormPhoneInput/form-phone-input';
import {
  appendCodeToPhoneNumber,
  removeCodeFromPhoneNumber,
} from 'src/components/Forms/utils';
import { PlatformUser } from 'src/graphql';
import {
  getPlatformUserAvatarStorageKey,
  getPlatformUserAvatarUrl,
} from 'src/graphql/entities/platform-user/platform-user-avatar-url';
import { useDeletePlatformUserAvatar } from 'src/graphql/entities/platform-user/use-delete-platform-user-avatar';
import {
  SignalNotificationOption,
  useManageUserNotificationPreferences,
  useSignalNotificationsList,
} from 'src/graphql/entities/user-communication-preferences';
import { Modals, useModalMethods, useModalState } from 'src/store/modals';
import { useNotifications } from 'src/utils/hooks/use-notifications';
import { UpdateUserFormFields, UpdateUserFormModel } from './types';

const notificationFormFields = [
  UpdateUserFormFields.PrimaryNotificationChannel,
  UpdateUserFormFields.SecondaryNotificationChannel,
  UpdateUserFormFields.TertiaryNotificationChannel,
];

const NOTIFICATION_STEP_PRIORITY = 10;
const MAX_NOTIFICATION_PRIORITY = 30;

export const UpdateUserModal = () => {
  const defaultSignalOptions = useSignalNotificationsList();
  const { closeModal } = useModalMethods(Modals.UpdateUser);
  const { isOpened, modalProps } = useModalState(Modals.UpdateUser);

  const { disabledFields = [] } = modalProps || {};

  const selectedPreferences = useSelectedNotificationOptions(
    defaultSignalOptions,
    modalProps?.user.userCommunicationPreferences,
  );

  const { canManageNotificationPreferences } = useManageUserNotificationPreferences();

  const notificationPreferencesAreVisible = useMemo(
    () => canManageNotificationPreferences(modalProps?.user),
    [modalProps?.user, canManageNotificationPreferences],
  );

  const formMethods = useForm<UpdateUserFormModel>({
    mode: 'all',
    values: {
      [UpdateUserFormFields.Id]: modalProps?.user?.id ?? '',
      [UpdateUserFormFields.FirstName]: modalProps?.user?.firstName ?? '',
      [UpdateUserFormFields.LastName]: modalProps?.user?.lastName ?? '',
      [UpdateUserFormFields.Email]: modalProps?.user?.email ?? '',
      [UpdateUserFormFields.PhoneNumber]: removeCodeFromPhoneNumber(
        modalProps?.user?.phoneNumber ?? '',
      ),
      [UpdateUserFormFields.AvatarFileKey]: modalProps?.user?.avatarFileKey ?? null,
      [UpdateUserFormFields.PrimaryNotificationChannel]: selectedPreferences[0] || null,
      [UpdateUserFormFields.SecondaryNotificationChannel]: selectedPreferences[1] || null,
      [UpdateUserFormFields.TertiaryNotificationChannel]: selectedPreferences[2] || null,
    },
  });

  const { formState, reset, control, handleSubmit, watch, setValue, getValues } = formMethods;

  const avatar = useAvatarSelection();

  const phoneNumber = watch(UpdateUserFormFields.PhoneNumber);

  useEffect(() => {
    /**
     * if phone number is not set we allow to select email option only and
     * clean all other options if they have phone call or sms selected
     */
    const phoneNumberIsNotSet = !phoneNumber && !getValues()[UpdateUserFormFields.PhoneNumber];
    if (phoneNumberIsNotSet) {
      const formData = getValues();
      const optionsWithPhoneNotRequired = !phoneNumber
        ? defaultSignalOptions.filter((item) => !item.requirePhone)
        : defaultSignalOptions;

      notificationFormFields.forEach((item) => {
        const formValue = formData[item] as SignalNotificationOption;
        const isValueValid = optionsWithPhoneNotRequired.find(
          (filterOptionItem) => filterOptionItem.value === formValue?.value,
        );

        if (!isValueValid) {
          setValue(item, null);
        }
      });
    }
  }, [phoneNumber, getValues, defaultSignalOptions, selectedPreferences, setValue]);

  const closeModalAndResetForm = useCallback(() => {
    reset();
    avatar.reset();
    closeModal();
  }, [reset, avatar, closeModal]);

  const { deleteAvatar } = useDeletePlatformUserAvatar();
  const { showErrorAlert } = useNotifications();

  const onFormSubmit = useCallback(
    async (formValues: UpdateUserFormModel) => {
      const flush = await avatar.flush(modalProps?.user?.partnerId);
      if (flush.status === AvatarFlushStatus.Failed) {
        if (flush.reason === AvatarFlushFailureReason.MissingPartner) {
          showErrorAlert('Cannot upload the photo: missing partner. Please reopen the form.');
        }
        return;
      }
      const avatarFileKey =
        flush.status === AvatarFlushStatus.Uploaded
          ? flush.fileKey
          : (formValues[UpdateUserFormFields.AvatarFileKey] ?? undefined);

      // phone numbers for platform users should be stored with a code
      const preparedFormValues: Partial<PlatformUser> = {
        [UpdateUserFormFields.Id]: formValues[UpdateUserFormFields.Id],
        [UpdateUserFormFields.FirstName]: formValues[UpdateUserFormFields.FirstName].trim(),
        [UpdateUserFormFields.LastName]: formValues[UpdateUserFormFields.LastName].trim(),
        [UpdateUserFormFields.PhoneNumber]: appendCodeToPhoneNumber(
          formValues[UpdateUserFormFields.PhoneNumber],
        ),
        [UpdateUserFormFields.AvatarFileKey]: avatarFileKey ?? undefined,
        userCommunicationPreferences: userNotificationOptionsToUserPreference(
          formValues,
          modalProps?.user.id!,
        ),
      };

      if (!formState.isDirty && flush.status !== AvatarFlushStatus.Uploaded) {
        closeModalAndResetForm();
        return;
      }
      try {
        await modalProps?.onSubmit({
          ...modalProps.user,
          ...preparedFormValues,
        });
        closeModalAndResetForm();
      } catch {
        if (flush.status === AvatarFlushStatus.Uploaded) {
          void deleteAvatar(
            getPlatformUserAvatarStorageKey(modalProps?.user?.partnerId, flush.fileKey),
          );
        }
      }
    },
    [
      avatar,
      modalProps,
      closeModalAndResetForm,
      formState.isDirty,
      deleteAvatar,
      showErrorAlert,
    ],
  );

  const onClose = useCallback(() => {
    if (formState.isSubmitting) {
      return;
    }
    closeModalAndResetForm();
  }, [closeModalAndResetForm, formState.isSubmitting]);

  return (
    <Dialog open={isOpened} onClose={onClose} fullWidth>
      <DialogTitle>
        <Typography>Update User</Typography>
      </DialogTitle>
      <DialogContent>
        <Stack flexDirection="column">
          <AvatarUploader
            name={getFullName(modalProps?.user)}
            imageUrl={
              avatar.previewUrl ??
              getPlatformUserAvatarUrl(
                modalProps?.user?.partnerId,
                modalProps?.user?.avatarFileKey,
              )
            }
            onFileChange={avatar.onFileChange}
          />

          <FormProvider {...formMethods}>
            <form
              style={{
                display: 'flex',
                flexDirection: 'column',
              }}
              onSubmit={handleSubmit(onFormSubmit)}
            >
              <FormControl
                variant="standard"
                sx={{
                  width: '100%',
                  padding: '0.5rem 0',
                }}
              >
                <Controller
                  control={control}
                  name={UpdateUserFormFields.FirstName}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <TextField
                      disabled={disabledFields.includes(UpdateUserFormFields.FirstName)}
                      sx={{
                        minWidth: '100%',
                      }}
                      placeholder="First Name"
                      error={!!formState.errors[UpdateUserFormFields.FirstName]}
                      {...field}
                    />
                  )}
                />
              </FormControl>

              <FormControl
                variant="standard"
                sx={{
                  width: '100%',
                  padding: '0.5rem 0',
                }}
              >
                <Controller
                  control={control}
                  name={UpdateUserFormFields.LastName}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <TextField
                      disabled={disabledFields.includes(UpdateUserFormFields.LastName)}
                      sx={{
                        minWidth: '100%',
                      }}
                      placeholder="Last Name"
                      error={!!formState.errors[UpdateUserFormFields.LastName]}
                      {...field}
                    />
                  )}
                />
              </FormControl>

              <FormControl
                variant="standard"
                sx={{
                  width: '100%',
                  padding: '0.5rem 0',
                }}
              >
                <Controller
                  control={control}
                  name={UpdateUserFormFields.Email}
                  disabled
                  render={({ field }) => (
                    <TextField
                      disabled={disabledFields.includes(UpdateUserFormFields.Email)}
                      sx={{
                        minWidth: '100%',
                      }}
                      {...field}
                    />
                  )}
                />
              </FormControl>

              <FormControl
                variant="standard"
                sx={{
                  width: '100%',
                  padding: '0.5rem 0',
                }}
              >
                <Controller
                  name={UpdateUserFormFields.PhoneNumber}
                  rules={{
                    minLength: MIN_NUMBER_LENGTH,
                    maxLength: MAX_NUMBER_LENGTH,
                  }}
                  control={control}
                  render={({ field }) => (
                    <StyledLabel>
                      <Input
                        {...field}
                        disabled={disabledFields.includes(UpdateUserFormFields.PhoneNumber)}
                        error={!!formState.errors[UpdateUserFormFields.PhoneNumber]}
                        placeholder={PHONE_FIELD_PLACEHOLDER}
                        type="tel"
                        inputComponent={TextMaskPhone}
                        disableUnderline
                        sx={{
                          minWidth: '100%',
                        }}
                        inputProps={{
                          sx: {
                            fontSize: '1rem',
                            paddingBlock: 2,
                          },
                        }}
                      />
                    </StyledLabel>
                  )}
                />
              </FormControl>

              {notificationPreferencesAreVisible ? (
                <>
                  <Typography
                    sx={{
                      margin: '1rem 0',
                      fontSize: '1rem',
                      fontWeight: 'bold',
                    }}
                  >
                    ProVision Signal Notification Preferences
                  </Typography>
                  <Typography
                    sx={{
                      marginBottom: '2rem',
                    }}
                  >
                    Set the order in which you would like escalation notifications to be sent
                    via ProVision Signal. Email is the default notification if no preferences
                    are set.
                  </Typography>

                  <FormAutocomplete
                    sx={{
                      marginBottom: '1rem',
                    }}
                    type="autocomplete"
                    name={UpdateUserFormFields.PrimaryNotificationChannel}
                    label="1st Notification"
                    options={defaultSignalOptions}
                    textFieldProps={{
                      size: 'small',
                      variant: 'outlined',
                    }}
                    autocompleteProps={{
                      disabled: disabledFields.includes(
                        UpdateUserFormFields.PrimaryNotificationChannel,
                      ),
                      filterOptions: (options, state) =>
                        filterAvailableOptions(options, getValues(), state),
                    }}
                  />

                  <FormAutocomplete
                    sx={{
                      marginBottom: '1rem',
                    }}
                    type="autocomplete"
                    name={UpdateUserFormFields.SecondaryNotificationChannel}
                    label="2nd Notification"
                    options={defaultSignalOptions}
                    textFieldProps={{
                      size: 'small',
                      variant: 'outlined',
                    }}
                    autocompleteProps={{
                      disabled: disabledFields.includes(
                        UpdateUserFormFields.SecondaryNotificationChannel,
                      ),
                      filterOptions: (options, state) =>
                        filterAvailableOptions(options, getValues(), state),
                    }}
                  />

                  <FormAutocomplete
                    sx={{
                      marginBottom: '1rem',
                    }}
                    type="autocomplete"
                    name={UpdateUserFormFields.TertiaryNotificationChannel}
                    label="3rd Notification"
                    options={defaultSignalOptions}
                    textFieldProps={{
                      size: 'small',
                      variant: 'outlined',
                    }}
                    autocompleteProps={{
                      disabled: disabledFields.includes(
                        UpdateUserFormFields.TertiaryNotificationChannel,
                      ),
                      filterOptions: (options, state) =>
                        filterAvailableOptions(options, getValues(), state),
                    }}
                  />
                </>
              ) : null}

              <UserModalActions
                onCancel={onClose}
                isSubmitting={formState.isSubmitting}
                isFormValid={formState.isValid}
              />
            </form>
          </FormProvider>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

function filterAvailableOptions(
  options: SignalNotificationOption[],
  formData: UpdateUserFormModel,
  state: FilterOptionsState<SignalNotificationOption>,
): SignalNotificationOption[] {
  const filteredOptionsByType = !formData[UpdateUserFormFields.PhoneNumber]
    ? options.filter((item) => !item.requirePhone)
    : options;

  return filteredOptionsByType.filter((item) =>
    item.label.toLowerCase().includes(state.inputValue?.toLocaleLowerCase()),
  );
}

function useSelectedNotificationOptions(
  defaultSignalOptions: SignalNotificationOption[],
  selectedPreferences?: UserCommunicationPreference[],
): (SignalNotificationOption | null)[] {
  return useMemo(() => {
    if (!selectedPreferences?.length) {
      return [];
    }

    return selectedPreferences.map((preference) => {
      const optionConfig = defaultSignalOptions.find(
        (item) => item.value === preference.communicationChannel,
      );

      return {
        value: preference.communicationChannel,
        id: preference.communicationChannel,
        label: optionConfig?.label ?? '',
        requirePhone: optionConfig?.requirePhone!,
      };
    });
  }, [defaultSignalOptions, selectedPreferences]);
}

function userNotificationOptionsToUserPreference(
  formData: UpdateUserFormModel,
  userId: string,
): UserCommunicationPreference[] {
  return notificationFormFields
    .map((item) => (formData[item] as SignalNotificationOption)?.value)
    .filter(Boolean)
    .map(
      (item, index) =>
        ({
          priority: MAX_NOTIFICATION_PRIORITY - NOTIFICATION_STEP_PRIORITY * index,
          communicationChannel: item,
          userId,
        }) as UserCommunicationPreference,
    );
}
